
import { IPill } from "@app/directives/_veri/v-pills/v-pills.component";
import { IReseller } from "@app/main/modules/foundation/_models/IReseller";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { AccountFleetType } from "@enums/account/AccountFleetType";
import { AccountMarketType } from "@enums/account/AccountMarketType";
import { AccountSector } from "@enums/account/AccountSector";
import { AccountStatus } from "@enums/account/AccountStatus";
import { Platform } from "@enums/account/Platform";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { UserPlatform } from "@interfaces/IUser";
import { CacheService } from "@services/cache.Service";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import { UserPreferencesDirectiveController } from "../userPreferences/userpreferences.directive";
import { ConstantsService } from "./constantsService";
import { DisplayService, IDialogOptionsJson } from "./display.service";
import { SessionService } from "./session.service";

export interface IShowPasswordEditorOptions {
    forcePasswordReset?: boolean;
    onCancelled?: () => void;
    onPasswordUpdated?: () => void;
}

export interface IAccount {
    Id: number;
    CompanyName: string;
    Image?: string;
    Features?: AccountFeatures;

    ContractExpiry?: string;
    DateTimeSetup?: string;

    Status?: AccountStatus;
    FleetType?: AccountFleetType;
    SectorId?: AccountSector;

    Platform?: Platform;

    ResellerId?: number;
    ResellerIds?: number[];

    FleetSize?: number;
    Website?: string;
    Phone?: string;
    MarketType?: AccountMarketType;

    // --

    platformString?: string;
    accountStatus?: string;
    resellerName?: string;
    featureString?: string;
    lastAccessed?: Date;
    resellerPills?: IPill<IReseller>[]
}

export class AccountService {

    static AccountFeaturesUpdatedEvent = "AccountFeaturesUpdatedEvent";

    isDemoSite: boolean;
    isMobile: boolean;

    private cacheName = "accountCache";

    static $inject = ["$q", "$rootScope", "$http", "$mdDialog", "cacheService", "displayService", "translationService", "constantsService", "sessionService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $http: angular.IHttpService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly cacheService: CacheService,
        private readonly displayService: DisplayService,
        private readonly translationService: TranslationService,
        private readonly constantsService: ConstantsService,
        private readonly sessionService: SessionService) {

        this.isDemoSite = this.constantsService.getIsDemoSite();
        this.isMobile = this.constantsService.getIsMobile();

        this.cacheService.createCache(this.cacheName);

        $rootScope.$on(AccountService.AccountFeaturesUpdatedEvent, () => { });
    }

    changePassword(password: string): angular.IPromise<any> {
        return this.$http({
            method: "POST",
            url: "/api/users/changepassword",
            data: JSON.stringify(password)
        });
    }

    getAccountDataFromCache(): IAccount {
        return this.cacheService.get(this.cacheName);
    }

    getAccountData(invalidateCache = false): angular.IPromise<IAccount> {
        if (this.$rootScope.currentUser === undefined || !this.$rootScope.currentUser.CurrentAccountUserMapping || this.$rootScope.currentUser?.platform === UserPlatform.Foundation) {
            return this.$q.resolve(null);
        }

        return this.cacheService.getOrRetrieveData<IAccount>(this.cacheName, () => this.getAccountDataFromDatabase(), invalidateCache);
    }

    createAccountMap(accounts): Map<number, IAccount> {
        return new Map(accounts.map(a => [a.Id, a]));
    }

    getAccountListFromDatabase(): angular.IPromise<IAccount[]> {
        return this
            .$http<IAccount[]>({
                url: "/api/accounts/",
                method: "GET",
                cache: true,
                params: { minsToLive: 30/*, invalidateCache: invalidateCache */ }
            })
            .then(response => {
                response.data.forEach(accountData => {
                    this.processAccountData(accountData, false);
                });

                return response.data;
            });
    }

    updateAccountData(): angular.IPromise<IAccount> {
        return this
            .$http<IAccount>({
                url: "/api/account/updatefeatures",
                method: "POST",
            })
            .then(response => {
                const accountData = response.data;
                this.processAccountData(accountData);

                return accountData;
            });
    }

    setAccountImage(base64ImageString: string, accountId?: number): angular.IPromise<any> {
        return this.$http({
            url: `/api/account/image/${accountId}`,
            method: "POST",
            data: `"${base64ImageString}"`
        });
    }

    showMyAccountDialog(event?: MouseEvent, passwordReset = false, emailRequired = false) {
        this.$mdDialog.show({
            template: require("../main/components/account/account.html"),
            targetEvent: event,
            multiple: true,
            escapeToClose: !passwordReset,
            clickOutsideToClose: false,
            controller: "accountController",
            controllerAs: "$ctrl",
            locals: { passwordReset: passwordReset, emailRequired: emailRequired }
        });
    }

    showMyPreferencesDialog(event?: MouseEvent, passwordReset = false, defaultLanguage = false) {
        const dialogOptions: angular.material.IDialogOptions = {
            template: this.isMobile ? require("../userPreferences/userpreferences.mobile.html") : require("../userPreferences/userpreferences.html"),
            targetEvent: event,
            escapeToClose: !passwordReset,
            multiple: true,
            clickOutsideToClose: false,
            controller: UserPreferencesDirectiveController,
            controllerAs: "$ctrl",
            locals: { passwordReset: passwordReset, defaultLanguage: defaultLanguage }
        };

        this.$mdDialog.show(dialogOptions);
    }

    showMyCompanyImageDialog(event?: MouseEvent) {
        return this.$mdDialog.show({
            template: require("../main/components/companyImage/companyImage.html"),
            targetEvent: event,
            multiple: true,
            escapeToClose: true,
            clickOutsideToClose: true,
            controller: "companyImageController",
            controllerAs: "$ctrl",
            locals: { accountId: null }
        });
    }

    showPasswordEditor($event: MouseEvent, options?: IShowPasswordEditorOptions) {
        const dialogOptions: IDialogOptionsJson = {
            title: this.translationService.t("AccountService.PasswordDialogTitle"),
            dialogHTML: "<password-editor " +
                "class='dialog-mode' " +
                "force-password-reset='$ctrl.forcePasswordReset' " +
                "on-cancelled='$ctrl.onCancelled(); $ctrl.hide();' " +
                "on-password-updated='$ctrl.onPasswordUpdated(); $ctrl.hide();'></password-editor>",
            dialogId: "passwordEditorComponent",
            customClass: `password-editor-dialog`,
            multiple: true,
            bindings: {
                forcePasswordReset: options?.forcePasswordReset,
                onCancelled: () => { options?.onCancelled() },
                onPasswordUpdated: () => { options?.onPasswordUpdated(); }
            },
            event: $event,
            preventFocusOnOpen: false,
            escapeToClose: !options?.forcePasswordReset,
            clickOutsideToClose: false,
            hideXCloseButton: true
        };

        this.displayService.showDialog(dialogOptions);
    }

    updateAccountCache(accountData: IAccount) {
        this.cacheService.save(this.cacheName, accountData);

        this.$rootScope.$broadcast(AccountService.AccountFeaturesUpdatedEvent);
    }

    getMultiAccountUserMappings(): angular.IPromise<IAccount[]> {
        return this
            .$http<IAccount[]>({
                url: "/api/account/usermapped",
                method: "GET"
            })
            .then(response => {
                response.data.forEach(accountData => {
                    this.processAccountData(accountData, false);
                });

                return response.data;
            });
    }


    //api/account/usermapped

    // ---

    private getAccountDataFromDatabase(): angular.IPromise<IAccount> {
        return this
            .$http<IAccount>({
                url: "/api/account/",
                method: "GET"
            })
            .then(response => {
                const accountData = response.data;

                if (accountData) {
                    this.processAccountData(accountData);
                }

                return accountData;
            })
            .catch(() => {
                // If this fails clear caches etc, the page will refresh shortly and present login page
                this.sessionService.clearAllCachesAndSessions();
                return null;
            });
    }

    private processAccountData(accountData: IAccount, updateCache = true) {

        if (this.isDemoSite) {
            accountData.CompanyName = "Demo Company";
        }

        if (accountData.Image) {
            accountData.Image = `data:image/png;base64,${accountData.Image}`;
        }

        // Todo
        // This currently doesnt work - requires Intranet changes.
        // So for now this is HARD CODED

        // Testing enabling features
        //this.enableFeature(accountData, AccountFeature.Can);
        //this.enableFeature(accountData, AccountFeature.Tracking);
        //this.enableFeature(accountData, AccountFeature.Camera);
        //this.enableFeature(accountData, AccountFeature.Tacho);

        // Testing disabling features
        //this.disableFeature(accountData, AccountFeature.Messaging);
        //this.disableFeature(accountData, AccountFeature.Camera);
        //this.disableFeature(accountData, AccountFeature.Temperature);
        //this.disableFeature(accountData, AccountFeature.Can);
        //this.disableFeature(accountData, AccountFeature.Tracking);
        //this.disableFeature(accountData, AccountFeature.Alcolock);
        //this.enableFeature(accountData, AccountFeature.Tacho)

        if (updateCache) {
            this.updateAccountCache(accountData);
        }
    }
}
